import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

const PublicationList = () => {
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 20;
  const history = useHistory();

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      console.error("User not logged in");
      return;
    }

    const fetchPublications = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/publications/admin/${userId}?page=${
            currentPage - 1
          }&size=${pageSize}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch publications");
        }
        const data = await response.json();

        // Update publications and pagination data
        const allPublications = data._embedded?.publications || [];
        setPublications(allPublications);
        setFilteredPublications(allPublications);
        setTotalPages(data.page?.totalPages || 0);
      } catch (error) {
        console.error("Error fetching publications:", error);
        setPublications([]);
        setFilteredPublications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPublications();
  }, [currentPage]); // Fetch new data when currentPage changes

  // Filter publications based on selected status
  useEffect(() => {
    if (status === "") {
      setFilteredPublications(publications);
    } else {
      setFilteredPublications(
        publications.filter((publication) => publication.status === status)
      );
    }
  }, [status, publications]);

  const handleSelectPublication = (publication) => {
    history.push(`/edit/${publication.id}`, { state: { publication } });
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "0":
        return "bg-warning text-dark";
      case "1":
        return "bg-success text-white";
      case "2":
        return "bg-danger text-white";
      default:
        return "";
    }
  };

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Publication List</h1>

        {/* Filter Dropdown */}
        <div className="d-flex align-items-center">
          <label htmlFor="status" className="form-label me-2 mb-0">
            Filter by Status:
          </label>
          <select
            id="status"
            className="form-select w-auto"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All</option>
            <option value="0">UnApproved</option>
            <option value="1">Approved</option>
            <option value="2">Archived</option>
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: "150px" }}>Pub ID</th>
              <th style={{ minWidth: "350px", width: "500px" }}>TITLE</th>
              <th style={{ width: "250px" }}>Indexing</th>
              <th style={{ width: "300px" }}>Publication</th>
              <th style={{ width: "150px" }}>Status</th>
              <th style={{ width: "200px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center">
                  Loading publications...
                </td>
              </tr>
            ) : filteredPublications.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No publications available.
                </td>
              </tr>
            ) : (
              filteredPublications.map((publication) => (
                <tr key={publication.id}>
                  <td>{publication.id}</td>
                  <td
                    style={{
                      whiteSpace: "normal",
                      wordWrap: "break-word",
                      maxWidth: "500px",
                    }}
                  >
                    {publication.title}
                  </td>
                  <td>{publication.indexing}</td>
                  <td>{publication.publication}</td>
                  <td>
                    <span
                      className={`badge ${getStatusColor(publication.status)}`}
                    >
                      {publication.status === "0"
                        ? "UnApproved"
                        : publication.status === "1"
                        ? "Approved"
                        : "Archived"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSelectPublication(publication)}
                    >
                      View/Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <button
          className="btn btn-outline-primary"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="btn btn-outline-primary"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PublicationList;
